import React, { useEffect, useMemo, useState, useRef } from 'react';
import './BlogPage.css';
import {
  getCurrentAuthenticatedUsername,
  getCurrentIdToken,
  getCurrentUserGroups,
  getCurrentUserProfile,
} from './cognitoAuth';
import {
  addBlogComment,
  createBlogPost,
  updateBlogPost,
  getBlogStorageMode,
  listBlogPosts,
  reactToBlogPost,
} from './blogService';

function formatDate(value) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmittingPost, setIsSubmittingPost] = useState(false);
  const [commentLoadingId, setCommentLoadingId] = useState('');
  const [reactionLoadingId, setReactionLoadingId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [authState, setAuthState] = useState({
    username: '',
    displayName: '',
    userKey: '',
    userPicture: '',
    groups: [],
    token: '',
  });

  const [postForm, setPostForm] = useState({ title: '', content: '' });
  const [postFormTags, setPostFormTags] = useState('');
  const editorRef = useRef(null);
  const [commentDrafts, setCommentDrafts] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPostId, setSelectedPostId] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function loadPage() {
      setIsLoading(true);
      setError('');

      try {
        const [loadedPosts, username, profile, groups, token] = await Promise.all([
          listBlogPosts(),
          getCurrentAuthenticatedUsername(),
          getCurrentUserProfile(),
          getCurrentUserGroups(),
          getCurrentIdToken(),
        ]);

        if (!isMounted) return;

        setPosts(loadedPosts);
        setAuthState({
          username: username || '',
          displayName: profile?.preferredUsername || username || '',
          userKey: profile?.userIdentifier || username || '',
          userPicture: profile?.picture || '',
          groups,
          token,
        });
      } catch (loadError) {
        if (isMounted) setError(loadError?.message || 'Unable to load the blog right now.');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    loadPage();
    return () => {
      isMounted = false;
    };
  }, []);

  const isRootUser = authState.groups.includes('Root_Users');
  const isDefaultUser = authState.groups.includes('Default');
  const canCreatePosts = isRootUser;
  const canInteract = isRootUser || isDefaultUser;
  const showRootOnlyInfo = isRootUser;
  const storageMode = getBlogStorageMode();

  const helperText = useMemo(() => {
    if (storageMode === 'api') return 'Connected to your configured AWS blog API.';
    return 'Running in browser-backed demo mode until REACT_APP_BLOG_API_BASE_URL is configured.';
  }, [storageMode]);

  const filteredPosts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return posts;

    return posts.filter((p) => {
      return (
        (p.title || '').toLowerCase().includes(term) ||
        (p.content || '').toLowerCase().includes(term) ||
        (p.authorName || '').toLowerCase().includes(term) ||
        (p.tags || '').toLowerCase().includes(term)
      );
    });
  }, [posts, searchTerm]);

  const selectedPost = useMemo(() => posts.find((p) => p.id === selectedPostId) || null, [posts, selectedPostId]);

  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const editEditorRef = useRef(null);
  const imageInputRef = useRef(null);
  const [editTags, setEditTags] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (selectedPost) {
      setEditTitle(selectedPost.title || '');
      setEditContent(selectedPost.content || '');
      setEditTags(selectedPost.tags || '');
      setEditing(false);
    }
  }, [selectedPost]);

  useEffect(() => {
    // populate editor DOM when editContent changes (when entering edit mode)
    if (editing && editEditorRef.current) {
      editEditorRef.current.innerHTML = editContent || '';
    }
    if (!editing && editorRef.current) {
      // ensure composer editor reflect postForm.content
      editorRef.current.innerHTML = postForm.content || '';
    }
  }, [editing, editContent, postForm.content]);

  function insertHtmlAtCursor(html, targetRef) {
    const root = targetRef.current;
    if (!root) return;
    root.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) {
      // append
      root.insertAdjacentHTML('beforeend', html);
      return;
    }
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const el = document.createElement('div');
    el.innerHTML = html;
    const frag = document.createDocumentFragment();
    let node, lastNode;
    while ((node = el.firstChild)) {
      lastNode = frag.appendChild(node);
    }
    range.insertNode(frag);
    // move caret after inserted
    if (lastNode) {
      range.setStartAfter(lastNode);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }

  function handleImageFiles(files, targetRef) {
    if (!files || files.length === 0) return;
    const file = files[0];
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result;
      const html = `<img src="${src}" class="rich-img" alt="Inserted image" />`;
      insertHtmlAtCursor(html, targetRef);
    };
    reader.readAsDataURL(file);
  }

  function onEditorClick(e, targetRef) {
    // if user clicks an image, cycle alignment classes
    const t = e.target;
    if (t && t.tagName === 'IMG' && t.classList) {
      if (t.classList.contains('align-left')) {
        t.classList.remove('align-left');
        t.classList.add('align-right');
      } else if (t.classList.contains('align-right')) {
        t.classList.remove('align-right');
        t.classList.add('full');
      } else if (t.classList.contains('full')) {
        t.classList.remove('full');
      } else {
        t.classList.add('align-left');
      }
    }
  }

  async function saveEdit() {
    if (!selectedPost) return;
    setUpdating(true);
    setError('');
    try {
      const updated = await updateBlogPost(selectedPost.id, {
        title: editTitle,
        content: editContent,
        tags: editTags,
        token: authState.token,
      });

      if (updated) {
        updatePosts(updated);
        setEditing(false);
        setMessage('Post updated.');
      }
    } catch (e) {
      setError(e?.message || 'Unable to update post.');
    } finally {
      setUpdating(false);
    }
  }

  function updatePosts(nextPost) {
    setPosts((currentPosts) => currentPosts.map((post) => (post.id === nextPost.id ? nextPost : post)));
  }

  function handlePostFormChange(event) {
    const { name, value } = event.target;
    setPostForm((current) => ({ ...current, [name]: value }));
  }

  function handleCommentDraftChange(postId, value) {
    setCommentDrafts((current) => ({ ...current, [postId]: value }));
  }

  async function handleCreatePost(event) {
    event.preventDefault();
    setError('');
    setMessage('');

    if (!canCreatePosts) {
      setError('Only Root_Users members can create new posts.');
      return;
    }

    if (!postForm.title.trim() || !postForm.content.trim()) {
      setError('Post title and content are required.');
      return;
    }

    setIsSubmittingPost(true);

    try {
      const createdPost = await createBlogPost({
        title: postForm.title.trim(),
        content: postForm.content.trim(),
        authorName: authState.displayName || authState.username || 'Root User',
        authorPicture: authState.userPicture || undefined,
        tags: postFormTags?.trim() || '',
        token: authState.token,
      });

      setPosts((currentPosts) => [createdPost, ...currentPosts]);
      setPostForm({ title: '', content: '' });
      setPostFormTags('');
      setMessage('Post published successfully.');
    } catch (createError) {
      setError(createError?.message || 'Unable to publish the post.');
    } finally {
      setIsSubmittingPost(false);
    }
  }

  async function handleCommentSubmit(event, postId) {
    event.preventDefault();
    setError('');
    setMessage('');

    if (!canInteract) {
      setError('You must be in the Default or Root_Users group to comment.');
      return;
    }

    const draft = (commentDrafts[postId] || '').trim();
    if (!draft) {
      setError('Comment text cannot be empty.');
      return;
    }

    setCommentLoadingId(postId);

    try {
      const updatedPost = await addBlogComment(postId, {
        body: draft,
        authorName: authState.displayName || authState.username || 'User',
        token: authState.token,
      });

      updatePosts(updatedPost);
      setCommentDrafts((current) => ({ ...current, [postId]: '' }));
      setMessage('Comment added.');
    } catch (commentError) {
      setError(commentError?.message || 'Unable to add your comment.');
    } finally {
      setCommentLoadingId('');
    }
  }

  async function handleReaction(postId, reaction) {
    setError('');
    setMessage('');

    if (!canInteract) {
      setError('You must be in the Default or Root_Users group to react to posts.');
      return;
    }

    setReactionLoadingId(`${postId}:${reaction}`);

    try {
      const updatedPost = await reactToBlogPost(postId, {
        reaction,
        userKey: authState.userKey || authState.username,
        token: authState.token,
      });

      updatePosts(updatedPost);
    } catch (reactionError) {
      setError(reactionError?.message || 'Unable to update your reaction.');
    } finally {
      setReactionLoadingId('');
    }
  }

  function userReacted(post, reaction) {
    if (!post) return false;
    const key = authState.userKey || authState.username || '';
    const arr = reaction === 'like' ? post.reactionUsers?.likes || [] : post.reactionUsers?.dislikes || [];
    return Array.isArray(arr) && arr.includes(key);
  }

  return (
    <main className="blog-page">
      <section className="blog-hero">
        <div>
          <span className="blog-hero-badge">Blog</span>
          <h1 className="blog-hero-title">Posts powered by Cognito roles</h1>
          {showRootOnlyInfo ? (
            <p className="blog-hero-text">
              Root_Users members can publish posts. Default members can join the conversation with comments and reactions.
            </p>
          ) : (
            <p className="blog-hero-text">Read posts and join the conversation with comments and reactions.</p>
          )}
        </div>
        {showRootOnlyInfo && (
          <div className="blog-hero-sidecard">
            <div className="blog-sidecard-label">Storage mode</div>
            <div className="blog-sidecard-value">{storageMode === 'api' ? 'AWS API' : 'Local demo'}</div>
            <p className="blog-sidecard-text">{helperText}</p>
            <div className="blog-group-pills">
              {authState.groups.length > 0 ? authState.groups.map((group) => <span key={group} className="blog-group-pill">{group}</span>) : <span className="blog-group-pill muted">Guest</span>}
            </div>
          </div>
        )}
      </section>

      <section className="blog-search">
        <input
          aria-label="Search posts and pages"
          className="blog-search-input"
          placeholder="Search posts or pages..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {searchTerm.trim() !== '' && (
          <div className="blog-search-results">
            {['Home', 'Blog', 'About', 'Contact', 'User Settings']
              .filter((t) => t.toLowerCase().includes(searchTerm.toLowerCase()))
              .map((t) => (
                <button key={t} type="button" className="blog-search-result" onClick={() => (window.location.assign ? window.location.assign(t === 'Home' ? '/' : `/${t.toLowerCase().replace(/ /g, '-')}`) : (window.location.href = '/'))}>
                  {t}
                </button>
              ))}

            {filteredPosts.slice(0, 6).map((p) => (
              <button key={p.id} type="button" className="blog-search-result" onClick={() => setSelectedPostId(p.id)}>
                {p.title}
              </button>
            ))}
          </div>
        )}
      </section>

      {canCreatePosts && (
        <section className="blog-composer">
          <div className="blog-section-heading-row">
            <div>
              <h2>Create a new post</h2>
              <p>Only Root_Users members see this editor.</p>
            </div>
          </div>
          <form className="blog-composer-form" onSubmit={handleCreatePost}>
            <input
              className="blog-input"
              name="title"
              type="text"
              value={postForm.title}
              onChange={handlePostFormChange}
              placeholder="Post title"
              maxLength={120}
            />
            <input
              className="blog-input"
              name="tags"
              type="text"
              value={postFormTags}
              onChange={(e) => setPostFormTags(e.target.value)}
              placeholder="tags (comma separated) - Root users only"
              maxLength={200}
            />
            <div className="blog-rich-toolbar">
              <button type="button" className="blog-secondary-button" onClick={() => imageInputRef.current && imageInputRef.current.click()}>Insert image</button>
              <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleImageFiles(e.target.files, editorRef)} />
              <small style={{ marginLeft: 8 }}>Click inserted images to cycle alignment (left → right → full → none)</small>
            </div>
            <div
              ref={editorRef}
              className="blog-rich-editor"
              contentEditable
              onInput={(e) => setPostForm((c) => ({ ...c, content: e.currentTarget.innerHTML }))}
              onClick={(e) => onEditorClick(e, editorRef)}
              role="textbox"
              aria-multiline="true"
            />
            <div className="blog-composer-actions">
              <button type="submit" className="blog-primary-button" disabled={isSubmittingPost}>
                {isSubmittingPost ? 'Publishing...' : 'Publish post'}
              </button>
            </div>
          </form>
        </section>
      )}

      {(error || message) && (
        <div className={`blog-feedback ${error ? 'error' : 'success'}`}>
          {error || message}
        </div>
      )}

      <section className="blog-feed">
        {selectedPost ? (
          <article className="blog-post-detail">
            <button type="button" className="blog-back-button" onClick={() => setSelectedPostId(null)}>
              ← Back to posts
            </button>
            <div className="post-row banner detail-banner">
              <div className="post-left">
                {(selectedPost.authorPicture || selectedPost.picture) ? (
                  <img src={selectedPost.authorPicture || selectedPost.picture} alt={selectedPost.authorName} className="post-avatar" />
                ) : (
                  <div className="post-avatar placeholder"><i className="fas fa-user"></i></div>
                )}
                <div className="post-author">
                  <div className="post-author-name">{selectedPost.authorName}</div>
                  {selectedPost.tags ? <div className="post-author-tag">{selectedPost.tags.split(',').map((t) => t.trim()).filter(Boolean).join(', ')}</div> : null}
                </div>
              </div>

              <div className="post-right">
                <div className="post-date">{formatDate(selectedPost.createdAt)}</div>
                <h2 className="post-title">{selectedPost.title}</h2>
                <div className="post-reaction-row small">
                  <button
                    type="button"
                    className={`blog-reaction-button small ${reactionLoadingId === `${selectedPost.id}:like` ? 'loading' : ''} ${userReacted(selectedPost, 'like') ? 'active' : ''}`}
                    onClick={() => handleReaction(selectedPost.id, 'like')}
                    aria-label="Like post"
                  >
                    <i className={`fas fa-thumbs-up`} aria-hidden="true" />
                    <span className="reaction-count">{(selectedPost.reactionUsers?.likes || []).length}</span>
                  </button>
                  <button
                    type="button"
                    className={`blog-reaction-button small ${reactionLoadingId === `${selectedPost.id}:dislike` ? 'loading' : ''} ${userReacted(selectedPost, 'dislike') ? 'active dislike' : ''}`}
                    onClick={() => handleReaction(selectedPost.id, 'dislike')}
                    aria-label="Dislike post"
                  >
                    <i className={`fas fa-thumbs-down`} aria-hidden="true" />
                    <span className="reaction-count">{(selectedPost.reactionUsers?.dislikes || []).length}</span>
                  </button>
                </div>
              </div>
            </div>
            {/* header removed - banner displays title/date/author */}
            {isRootUser && !editing && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button type="button" className="blog-secondary-button" onClick={() => setEditing(true)}>Edit post</button>
              </div>
            )}

            {editing ? (
              <div className="blog-post-edit">
                <input className="blog-input" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
                <input className="blog-input" value={editTags} onChange={(e) => setEditTags(e.target.value)} placeholder="tags, comma separated" />
                <div className="blog-rich-toolbar">
                  <button type="button" className="blog-secondary-button" onClick={() => imageInputRef.current && imageInputRef.current.click()}>Insert image</button>
                  <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleImageFiles(e.target.files, editEditorRef)} />
                  <small style={{ marginLeft: 8 }}>Click inserted images to cycle alignment (left → right → full → none)</small>
                </div>
                <div
                  ref={editEditorRef}
                  className="blog-rich-editor"
                  contentEditable
                  onInput={(e) => setEditContent(e.currentTarget.innerHTML)}
                  onClick={(e) => onEditorClick(e, editEditorRef)}
                  role="textbox"
                  aria-multiline="true"
                  style={{ minHeight: 240 }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="blog-primary-button" onClick={saveEdit} disabled={updating}>Save</button>
                  <button type="button" className="blog-secondary-button" onClick={() => setEditing(false)} disabled={updating}>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                {selectedPost.imageUrl && <img src={selectedPost.imageUrl} alt="post" className="blog-post-image" />}
                <p className="blog-post-content">{selectedPost.content}</p>
              </>
            )}

            <section className="blog-comments-section">
              <h3>Comments</h3>
              {selectedPost.comments.length === 0 ? (
                <p className="blog-comments-empty">No comments yet.</p>
              ) : (
                <div className="blog-comment-list">
                  {selectedPost.comments.map((comment) => (
                    <div key={comment.id} className="blog-comment-card">
                      <div className="blog-comment-meta">
                        <strong>{comment.authorName}</strong>
                        <span>{formatDate(comment.createdAt)}</span>
                      </div>
                      <p>{comment.body}</p>
                    </div>
                  ))}
                </div>
              )}
              <form className="blog-comment-form" onSubmit={(event) => { event.preventDefault(); handleCommentSubmit(event, selectedPost.id); }} style={{ marginTop: 12 }}>
                <textarea
                  className="blog-textarea comment"
                  value={commentDrafts[selectedPost.id] || ''}
                  onChange={(e) => handleCommentDraftChange(selectedPost.id, e.target.value)}
                  placeholder={canInteract ? 'Share your thoughts...' : 'Sign in to comment'}
                  rows={4}
                  disabled={!canInteract || commentLoadingId === selectedPost.id}
                />
                <div style={{ marginTop: 8 }}>
                  <button type="submit" className="blog-secondary-button" disabled={!canInteract || commentLoadingId === selectedPost.id}>
                    {commentLoadingId === selectedPost.id ? 'Posting...' : 'Add comment'}
                  </button>
                </div>
              </form>
            </section>
          </article>
        ) : isLoading ? (
          <div className="blog-empty-state">Loading blog posts...</div>
        ) : posts.length === 0 ? (
          <div className="blog-empty-state">No posts yet.</div>
        ) : (
          filteredPosts.map((post) => {
            return (
              <article key={post.id} className="blog-post-card">
                <div className="post-row banner" onClick={() => setSelectedPostId(post.id)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') setSelectedPostId(post.id); }}>
                  <div className="post-left">
                    {(post.authorPicture || post.picture) ? (
                      <img src={post.authorPicture || post.picture} alt={post.authorName} className="post-avatar" />
                    ) : (
                      <div className="post-avatar placeholder"><i className="fas fa-user"></i></div>
                    )}
                    <div className="post-author">
                      <div className="post-author-name">{post.authorName}</div>
                      {post.tags ? <div className="post-author-tag">{post.tags.split(',').map((t) => t.trim()).filter(Boolean).join(', ')}</div> : null}
                    </div>
                  </div>

                  <div className="post-right">
                    <div className="post-date">{formatDate(post.createdAt)}</div>
                    <h3 className="post-title">{post.title}</h3>
                    <div className="post-reaction-row small">
                      <button
                        type="button"
                        className={`blog-reaction-button small ${reactionLoadingId === `${post.id}:like` ? 'loading' : ''} ${userReacted(post, 'like') ? 'active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); handleReaction(post.id, 'like'); }}
                        aria-label="Like post"
                      >
                        <i className={`fas fa-thumbs-up`} aria-hidden="true" />
                        <span className="reaction-count">{(post.reactionUsers?.likes || []).length}</span>
                      </button>
                      <button
                        type="button"
                        className={`blog-reaction-button small ${reactionLoadingId === `${post.id}:dislike` ? 'loading' : ''} ${userReacted(post, 'dislike') ? 'active dislike' : ''}`}
                        onClick={(e) => { e.stopPropagation(); handleReaction(post.id, 'dislike'); }}
                        aria-label="Dislike post"
                      >
                        <i className={`fas fa-thumbs-down`} aria-hidden="true" />
                        <span className="reaction-count">{(post.reactionUsers?.dislikes || []).length}</span>
                      </button>
                    </div>
                  </div>
                  <div className="post-actions">
                    <button
                      type="button"
                      className="blog-secondary-button view-post-button"
                      onClick={(e) => { e.stopPropagation(); setSelectedPostId(post.id); }}
                    >
                      View post
                    </button>
                  </div>
                </div>
              </article>
            );
          })
        )}
      </section>
    </main>
  );
}

export default BlogPage;